import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  PhoneCall,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import { apiClient } from '@/lib/api';
import type { AdminOverview } from '@/lib/types';
import {
  OverviewDrilldownDrawer,
  type DrilldownKind,
} from '@/components/overview/overview-drilldown-drawer';
import { KpiWorkspace } from '@/components/overview/kpi-workspace';
import { Card } from '@/components/ui/card';
import { QcResultBadge } from '@/components/ui/status-badge';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui/empty-state';
import {
  formatDateTime,
  formatPercent,
  formatScore,
  formatSignedDelta,
} from '@/lib/format';

const DEPT_COLORS = [
  '#f97316',
  '#fb923c',
  '#facc15',
  '#34d399',
  '#38bdf8',
  '#a855f7',
];

const KPI_GRADIENTS = {
  orange: 'from-[#ff8a4c] via-[#ff6a3d] to-[#f04438]',
  sky: 'from-[#5eafff] via-[#3b82f6] to-[#2563eb]',
  emerald: 'from-[#34d399] via-[#10b981] to-[#0d9488]',
  violet: 'from-[#fbbf24] via-[#f59e0b] to-[#a855f7]',
} as const;

type KpiColor = keyof typeof KPI_GRADIENTS;

interface KpiCardProps {
  title: string;
  value: string;
  deltaLabel: string;
  delta: number | null;
  hint?: string;
  color: KpiColor;
  icon: React.ReactNode;
  /** Issue #260：提供时该 KPI 卡可点击下钻明细（在线员工 / 今日通话）。 */
  onOpen?: () => void;
}

function KpiCard({ title, value, deltaLabel, delta, hint, color, icon, onOpen }: KpiCardProps) {
  const deltaText = delta === null ? '--' : formatSignedDelta(delta);
  const positive = delta !== null && delta >= 0;
  const clickable = typeof onOpen === 'function';
  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl bg-gradient-to-br p-5 text-white shadow-lg shadow-slate-900/10',
        KPI_GRADIENTS[color],
        clickable &&
          'cursor-pointer transition hover:shadow-xl hover:ring-2 hover:ring-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
      )}
      data-testid={`kpi-card-${color}`}
      {...(clickable
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: onOpen,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            },
            'aria-label': `${title}，点击查看明细`,
          }
        : {})}
    >
      <div
        aria-hidden
        className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/15 blur-xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-black/10 blur-2xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/25 text-white ring-1 ring-white/30 backdrop-blur">
          {icon}
        </div>
        <div
          className={clsx(
            'inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium text-white ring-1 ring-white/30',
            delta === null && 'opacity-60',
          )}
        >
          {delta === null ? null : positive ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          <span>
            {deltaLabel} {deltaText}
          </span>
        </div>
      </div>
      <div className="relative mt-5 text-3xl font-bold tracking-tight">{value}</div>
      <div className="relative mt-1 text-sm font-medium text-white/90">{title}</div>
      {hint ? (
        <div className="relative mt-1 text-[11px] text-white/70">{hint}</div>
      ) : null}
      {clickable ? (
        <div className="relative mt-2 text-[11px] font-medium text-white/85">
          点击查看明细 ›
        </div>
      ) : null}
    </div>
  );
}

export function OverviewPage() {
  // Issue #260：在线员工 / 今日通话 KPI 卡下钻抽屉。
  const [drill, setDrill] = useState<DrilldownKind>(null);
  const query = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => apiClient.get<AdminOverview>('/admin/overview'),
  });

  const trendData = useMemo(
    () =>
      (query.data?.trend ?? []).map((p) => ({
        date: p.date.slice(5),
        calls: p.callCount,
      })),
    [query.data?.trend],
  );

  const distributionData = useMemo(
    () =>
      (query.data?.scoreDistribution ?? []).map((b) => ({
        bucket: `${b.bucket}分`,
        count: b.count,
      })),
    [query.data?.scoreDistribution],
  );

  const maxDeptCalls = useMemo(() => {
    if (!query.data?.departmentRanking?.length) return 0;
    return Math.max(...query.data.departmentRanking.map((d) => d.callCount));
  }, [query.data?.departmentRanking]);

  return (
    <div className="flex flex-col gap-6">
      <header
        className="flex flex-wrap items-center justify-between gap-3"
        data-testid="overview-header"
      >
        <div>
          <div className="text-xs text-slate-500">首页 / 数据总览</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">数据总览</h1>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {query.data?.generatedAt ? (
            <span>数据更新于 {formatDateTime(query.data.generatedAt)}</span>
          ) : null}
          <span
            className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-slate-600 shadow-sm ring-1 ring-slate-200"
            data-testid="overview-bell"
          >
            <Bell className="h-3.5 w-3.5 text-accent-500" />
            待处理 {query.data?.pendingItems?.length ?? 0}
          </span>
        </div>
      </header>

      {query.isLoading ? (
        <Card>
          <LoadingState label="正在拉取后台聚合数据…" />
        </Card>
      ) : query.isError ? (
        <Card>
          <ErrorState
            title="后台聚合接口加载失败"
            description={query.error instanceof Error ? query.error.message : '请稍后重试或联系管理员'}
            retry={() => query.refetch()}
          />
        </Card>
      ) : query.data ? (
        <OverviewContent
          data={query.data}
          trendData={trendData}
          distributionData={distributionData}
          maxDeptCalls={maxDeptCalls}
          onDrill={setDrill}
        />
      ) : null}

      {/* Issue #346：员工 KPI 考核版块（日/周/月 + 搜索 + 部门 + 分页），独立于上方聚合卡加载。 */}
      <KpiWorkspace />

      <OverviewDrilldownDrawer kind={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

interface OverviewContentProps {
  data: AdminOverview;
  trendData: Array<{ date: string; calls: number }>;
  distributionData: Array<{ bucket: string; count: number }>;
  maxDeptCalls: number;
  onDrill: (kind: DrilldownKind) => void;
}

function OverviewContent({
  data,
  trendData,
  distributionData,
  maxDeptCalls,
  onDrill,
}: OverviewContentProps) {
  return (
    <>
      <section
        className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
        data-testid="kpi-grid"
      >
        <KpiCard
          title="今日通话"
          value={`${data.kpi.todayCalls}次`}
          deltaLabel="较昨日"
          delta={data.kpi.todayCallsDeltaPercent}
          hint="按 UTC 自然日聚合"
          color="orange"
          icon={<PhoneCall className="h-5 w-5" />}
          onOpen={() => onDrill('today')}
        />
        <KpiCard
          title="在线员工"
          value={`${data.kpi.onlineEmployees}人`}
          deltaLabel="较上月"
          delta={data.kpi.onlineEmployeesDelta}
          hint="近 30 分钟活跃账号"
          color="sky"
          icon={<Users className="h-5 w-5" />}
          onOpen={() => onDrill('online')}
        />
        <KpiCard
          title="平均合规率"
          value={formatPercent(data.kpi.avgComplianceRate)}
          deltaLabel="较上月"
          delta={data.kpi.avgComplianceRateDelta}
          hint="本月质检通过率"
          color="emerald"
          icon={<ShieldCheck className="h-5 w-5" />}
        />
        <KpiCard
          title="平均得分"
          value={data.kpi.avgScore === null ? '--' : `${formatScore(data.kpi.avgScore)}分`}
          deltaLabel="较上月"
          delta={data.kpi.avgScoreDelta}
          hint="本月 AI 评分平均值"
          color="violet"
          icon={<Star className="h-5 w-5" />}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="近 7 天通话趋势" description="按自然日聚合的通话总次数">
          <div className="h-64" data-testid="trend-chart">
            {trendData.every((p) => p.calls === 0) ? (
              <EmptyState
                title="近 7 天暂无通话数据"
                description="一旦员工 APP 开始上传录音，这里就会出现折线。"
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    fontSize={12}
                    interval={0}
                    padding={{ left: 12, right: 12 }}
                  />
                  <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="calls"
                    stroke="#f56a17"
                    strokeWidth={2.6}
                    dot={{ r: 4, fill: '#f56a17', stroke: '#fff', strokeWidth: 1.5 }}
                    activeDot={{ r: 6 }}
                    name="通话次数"
                    isAnimationActive={false}
                    label={{
                      position: 'top',
                      fill: '#475569',
                      fontSize: 11,
                      offset: 8,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <Card title="得分分布" description="本月质检综合分布桶（单位：通）">
          <div className="h-64" data-testid="distribution-chart">
            {distributionData.every((b) => b.count === 0) ? (
              <EmptyState
                title="尚无质检报告"
                description="生成质检报告后，这里会出现得分直方图。"
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionData} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="bucket" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      fontSize: 12,
                    }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[6, 6, 0, 0]}
                    name="通话数"
                    isAnimationActive={false}
                    label={{ position: 'top', fill: '#475569', fontSize: 11 }}
                  >
                    {distributionData.map((entry, idx) => (
                      <Cell
                        key={entry.bucket}
                        fill={['#fb923c', '#fbbf24', '#34d399', '#3b82f6', '#a855f7'][idx % 5]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card
          title="待处理事项"
          description="reviewStatus = PENDING_REVIEW 的最近 10 条质检报告"
          action={
            <Link
              to="/calls?status=PROCESSING"
              className="text-xs font-medium text-accent-600 hover:underline"
            >
              查看全部
            </Link>
          }
        >
          {data.pendingItems.length === 0 ? (
            <EmptyState
              title="暂无待处理事项"
              description="所有质检报告都已被复核完毕。"
            />
          ) : (
            <div className="overflow-x-auto" data-testid="pending-table">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="text-xs text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">员工</th>
                    <th className="px-2 py-2 text-left font-medium">通话时间</th>
                    <th className="px-2 py-2 text-left font-medium">得分</th>
                    <th className="px-2 py-2 text-left font-medium">状态</th>
                    <th className="px-2 py-2 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {data.pendingItems.map((item) => (
                    <tr key={item.callRecordId} className="hover:bg-slate-50">
                      <td className="px-2 py-2 font-medium text-slate-800">{item.employeeName}</td>
                      <td className="px-2 py-2 text-slate-500">
                        {formatDateTime(item.occurredAt)}
                      </td>
                      <td className="px-2 py-2 font-semibold text-accent-600">
                        {formatScore(item.aiScore)}
                      </td>
                      <td className="px-2 py-2">
                        <QcResultBadge result={item.qcResult} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link
                          to={`/calls/${item.callRecordId}`}
                          className="text-xs font-medium text-accent-600 hover:underline"
                        >
                          查看报告
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="部门排行榜" description="本月展示全部有效部门，按平均得分排序；无本月通话部门显示 0 通 / 暂无评分">
          {/* Issue #230：员工无部门诊断——提示有员工未分配部门，其通话不计入部门排行。 */}
          {(data.unassignedEmployeeCount ?? 0) > 0 ? (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              有 {data.unassignedEmployeeCount} 名在职员工未分配部门，其通话不计入部门排行。请在「员工管理」为其设置部门。
            </div>
          ) : null}
          {data.departmentRanking.length === 0 ? (
            // Issue #208：按后端归因区分空态——避免「明明已建部门」却仍提示「尚未配置部门」。
            (() => {
              const reason = data.departmentRankingEmptyReason ?? 'NO_DEPARTMENTS';
              if (reason === 'NO_CALL_DATA') {
                return (
                  <EmptyState
                    title="本月暂无通话数据"
                    description="部门架构已配置且员工已绑定部门，待本月产生质检通话后将展示排行榜。"
                  />
                );
              }
              if (reason === 'NO_BOUND_EMPLOYEES') {
                return (
                  <EmptyState
                    title="员工尚未绑定部门"
                    description="部门架构已配置，但还没有员工归属到部门。请在「员工管理」为员工设置部门，或运行部门回填脚本。"
                  />
                );
              }
              return (
                <EmptyState
                  title="尚未配置部门"
                  description="请先在「员工管理 / 系统设置」创建部门架构（或运行部门回填脚本），并将员工绑定到部门后展示排行榜。"
                />
              );
            })()
          ) : (
            <ol className="flex flex-col gap-3" data-testid="dept-ranking">
              {data.departmentRanking.map((dept, idx) => {
                const ratio =
                  maxDeptCalls > 0 ? Math.max(0.08, dept.callCount / maxDeptCalls) : 0;
                const trophyColors = [
                  'bg-gradient-to-br from-amber-400 to-amber-600',
                  'bg-gradient-to-br from-slate-300 to-slate-500',
                  'bg-gradient-to-br from-orange-400 to-orange-600',
                ];
                return (
                  <li key={dept.deptId} className="flex items-center gap-3">
                    <div
                      className={clsx(
                        'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white shadow-sm',
                        trophyColors[idx] ?? 'bg-slate-300 text-slate-700',
                      )}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="font-medium text-slate-900">
                          {dept.deptName}
                          {/* Issue #230：部门无绑定员工诊断。 */}
                          {dept.employeeCount === 0 ? (
                            <span className="ml-1.5 text-xs text-amber-600">（无绑定员工）</span>
                          ) : null}
                        </span>
                        <span className="text-xs text-slate-500">
                          {/* Issue #230：无本月通话显示 0 通 / 暂无评分。 */}
                          {dept.callCount} 通 ·{' '}
                          {dept.avgScore === null ? '暂无评分' : `均分 ${formatScore(dept.avgScore)}`}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(ratio * 100).toFixed(1)}%`,
                            backgroundColor: DEPT_COLORS[idx % DEPT_COLORS.length],
                          }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      </section>
    </>
  );
}
